/**
 * Every URL that points INTO an event, built in one place.
 *
 * Before this file the slugs lived in three copies. `AttendeeEventPage` owned
 * the real `TAB_SLUGS`; `notification-links.ts` hand-wrote `kunngjoringer` and
 * `chat` under a comment saying they were «the FIXED Norwegian slugs from
 * AttendeeEventPage's TAB_SLUGS»; and `attention.ts` wrote no slug at all —
 * every one of its rows linked to bare `/event/:id` while its own context line
 * named a tab, so «3 mangler rom · Overnatting» opened Oversikt. A reader who
 * clicked the row got the promise and not the place.
 *
 * The copies are the reason the third one could be wrong without anybody
 * noticing: there was nothing to be inconsistent WITH. Now the map is typed by
 * the tab union, so a tab added without a slug fails the build rather than
 * silently linking nowhere.
 *
 * The slugs are fixed Norwegian strings and are never translated — a link
 * pasted into a chat has to open the same tab regardless of the reader's
 * language, and links in already-sent email have to keep working.
 */

export type EventTabKey =
  | 'overview'
  | 'program'
  | 'my_choices'
  | 'logistics'
  | 'announcements'
  | 'gallery'
  // Appended when the viewer may administer. Same flat row, one shield apart.
  | 'attendees'
  | 'responses'
  | 'rooms'
  | 'poll'
  | 'tasks'
  | 'groupings'
  | 'vouchers'
  | 'chat'

/** Tab ↔ URL slug for `/event/:id?t=…`. Exhaustive by type. */
export const EVENT_TAB_SLUGS: Record<EventTabKey, string> = {
  overview: 'oversikt',
  program: 'program',
  my_choices: 'mine-valg',
  logistics: 'logistikk',
  announcements: 'kunngjoringer',
  gallery: 'galleri',
  chat: 'chat',
  attendees: 'deltakere',
  responses: 'skjemaer',
  rooms: 'rom',
  poll: 'datovalg',
  tasks: 'oppgaver',
  groupings: 'grupper',
  vouchers: 'bonger',
}

export const SLUG_TO_EVENT_TAB: Record<string, EventTabKey> = Object.fromEntries([
  ...Object.entries(EVENT_TAB_SLUGS).map(([k, v]) => [v, k as EventTabKey]),
  ['svar', 'responses'],
]) as Record<string, EventTabKey>

/**
 * The query param `FormsAdminTab` reads to open ONE named form.
 *
 * The dashboard can raise five «80 ubesvarte skjema» rows against one event,
 * one per form, each naming a different form in its context line. Without this
 * they were five links to the same place, and that place showed whichever form
 * happened to be first. `get_my_open_issues()` has returned the form id as
 * `subject_id` the whole time; the client threw it away.
 */
export const EVENT_FORM_PARAM = 'f'

/** The selected grouping inside the admin Groups workspace. */
export const EVENT_GROUPING_PARAM = 'g'
export const EVENT_PERSON_PARAM = 'person'
export const EVENT_ROSTER_FILTER_PARAM = 'filter'
/** One date on an event with dates (`event_grouping_buckets.id`), as the roster's date filter. */
export const ROSTER_DATE_PARAM = 'dato'
export const EVENT_SEARCH_PARAM = 'q'

export function eventParticipantHref(eventId: string, email: string) {
  return eventHref(eventId, 'attendees', { [EVENT_PERSON_PARAM]: email })
}

export function eventGroupingHref(eventId: string, groupingId: string) {
  return eventHref(eventId, 'groupings', { [EVENT_GROUPING_PARAM]: groupingId })
}

/** `/event/:id`, optionally on a named tab, optionally with extra params. */
export function eventHref(
  eventId: string,
  tab?: EventTabKey,
  extra?: Record<string, string | null | undefined>,
): string {
  const params = new URLSearchParams()
  if (tab) params.set('t', EVENT_TAB_SLUGS[tab])
  for (const [key, value] of Object.entries(extra ?? {})) {
    if (value) params.set(key, value)
  }
  const query = params.toString()
  return query ? `/event/${eventId}?${query}` : `/event/${eventId}`
}

export type EventEditTabKey =
  'details' | 'location' | 'settings' | 'appearance' | 'registration' | 'team' | 'share' | 'danger' |
  'rsvp' | 'visibility' | 'features' | 'practical'

/** Runtime counterpart of `EventEditTabKey`, for validating `?tab=` off a URL. */
export const EVENT_EDIT_TABS: EventEditTabKey[] = [
  'details',
  'location',
  'settings',
  'appearance',
  'registration',
  'team',
  'share',
  'danger',
  'rsvp',
  'visibility',
  'features',
  'practical',
]

/**
 * `/event/:id/edit`, optionally on a named tab.
 *
 * The lifecycle status picker (draft → published) sits in the editor's
 * persistent ObjectBar rather than inside any one tab, so every edit URL can
 * publish. The tab only decides what is under it.
 */
export function eventEditHref(eventId: string, tab?: EventEditTabKey, returnTo?: string): string {
  const search = new URLSearchParams()
  if (tab) search.set('tab', tab)
  if (returnTo) search.set('returnTo', returnTo)
  return `/event/${eventId}/edit${search.size ? `?${search}` : ''}`
}

/** The existing offer-mode surface retains a directly addressable selected view. */
export function eventOfferHref(
  eventId: string,
  view: 'overview' | 'responses' | 'activity' | 'admin' = 'overview',
) {
  return eventHref(eventId, undefined, { offerView: view })
}

/**
 * Is this `:id` segment something that can BE an event?
 *
 * ## F-08 · «/event/create» rendered a data error, not a 404
 *
 * `/event/:id` is a dynamic route, so any URL of that shape reaches
 * `EventDetailPage`, which asks the database for an event whose id is the
 * literal string. The reader typed a URL that does not exist and got
 * «Noe gikk galt · Prøv igjen» — a retry button on a permanent outcome, which
 * is the one thing `EventDetailPage`'s own comment says a not-found must never
 * offer.
 *
 * The obvious fix is a list of reserved words. It is the wrong one: the list is
 * correct only until somebody adds a route, and nothing makes them remember.
 *
 * `events.id` is `uuid not null default gen_random_uuid()`, so the shape IS the
 * answer, and it is exhaustive by construction: every static slug that will
 * ever be added to `/event/*` fails this test without anyone maintaining
 * anything. Any-version UUID is accepted deliberately — this is a routing
 * question («could this address name a row?»), not a validation of how the row
 * was generated, and the database remains the authority on whether it exists.
 */
export function isEventId(id: string | undefined | null): boolean {
  return !!id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
}

export type EventFormView = 'edit' | 'responses' | 'share'
export const EVENT_FORM_VIEW_PARAM = 'visning'
export const EVENT_FORM_VIEWS: Record<EventFormView, string> = {
  edit: 'rediger',
  responses: 'svar',
  share: 'del',
}

export function eventFormHref(
  eventId: string,
  formId?: string | null,
  view: EventFormView = 'edit',
) {
  return eventHref(eventId, 'responses', {
    [EVENT_FORM_PARAM]: formId,
    [EVENT_FORM_VIEW_PARAM]: formId ? EVENT_FORM_VIEWS[view] : null,
  })
}

/**
 * ## The `/kommunikasjon` side of the same map — ER-06
 *
 * `KommunikasjonPage` owned `TAB_SLUGS` the way `AttendeeEventPage` once owned
 * the event slugs, and every link INTO the module was hand-written against it
 * from memory. Measured before this change, six call sites wrote the URL by
 * hand and three of them were wrong about context:
 *
 *   - `AttendeeEventPage` «Åpne kommunikasjon» →  `/kommunikasjon`
 *   - `AgendaTab` «Se status»                 →  `?fane=sendinger`
 *   - `AnnouncementsTab` receipt report       →  `?fane=rapporter`
 *
 * All three are reached from INSIDE an event and all three dropped the event,
 * so the module rendered with no return button and the organiser landed in a
 * list of every send she has ever made. The two that were right
 * (`EventNotificationScheduleModal`, `ScheduleEvaluationPublishModal`) were
 * right by copying, which is the same accident the event-slug comment above
 * describes: nothing to be inconsistent WITH.
 *
 * `event` is RETURN CONTEXT, not a filter. `KommunikasjonPage` renders the
 * back-link from it on every tab, and only «Ny sending» reads it as a
 * pre-selection. That is why carrying it on `sendinger` and `rapporter` adds a
 * way back without changing what either tab shows.
 */
export type CommsTabKey = 'broadcasts' | 'new' | 'reports' | 'contacts' | 'senders'

/** Tab ↔ URL slug for `/kommunikasjon?fane=…`. Exhaustive by type. */
export const COMMS_TAB_SLUGS: Record<CommsTabKey, string> = {
  broadcasts: 'sendinger',
  new: 'ny',
  reports: 'rapporter',
  contacts: 'kontakter',
  senders: 'avsendere',
}

export const SLUG_TO_COMMS_TAB: Record<string, CommsTabKey | undefined> = Object.fromEntries(
  Object.entries(COMMS_TAB_SLUGS).map(([k, v]) => [v, k as CommsTabKey]),
)

export const COMMS_TAB_PARAM = 'fane'
export const COMMS_EVENT_PARAM = 'event'

/** `/kommunikasjon` on a named tab, with no event context. */
export function commsHref(tab: CommsTabKey): string {
  return `/kommunikasjon?${COMMS_TAB_PARAM}=${COMMS_TAB_SLUGS[tab]}`
}

/**
 * `/kommunikasjon` on a named tab, carrying the event you came from.
 *
 * Param order is fixed — `fane`, then `event`, then extras — because links of
 * this exact shape are already in sent email and in tests that assert the
 * whole string.
 */
export function eventCommsHref(
  eventId: string,
  tab: CommsTabKey,
  extra?: Record<string, string | null | undefined>,
): string {
  const params = new URLSearchParams({ [COMMS_TAB_PARAM]: COMMS_TAB_SLUGS[tab], [COMMS_EVENT_PARAM]: eventId })
  for (const [key, value] of Object.entries(extra ?? {})) {
    if (value) params.set(key, value)
  }
  return `/kommunikasjon?${params}`
}

/**
 * «Åpne kommunikasjon» from an event: the send HISTORY, event still attached.
 *
 * Deliberately not the composer. The button sits beside «Ny SMS», so the pair
 * has to mean two different things; this one answers «what has already gone
 * out for this event?» and leaves a way back to it.
 */
export function eventCommsHistoryHref(eventId: string) {
  return eventCommsHref(eventId, 'broadcasts')
}

/** The delivery-receipt report, event still attached. */
export function eventCommsReportHref(eventId: string) {
  return eventCommsHref(eventId, 'reports')
}

/** Context only; the composer still validates event/form access on the server. */
export function eventSmsHref(eventId: string, formId?: string, noncompleters = false) {
  return eventCommsHref(eventId, 'new', {
    form: formId,
    audience: formId && noncompleters ? 'noncompleters' : null,
  })
}

export function eventGroupSmsHref(eventId: string, groupingId: string, bucketId?: string | null) {
  return eventCommsHref(eventId, 'new', { group: groupingId, slot: bucketId })
}

/** Overview links stay on the actual public data path while previewing. */
export function eventSurfaceHref(
  surface: { pathname: string; search: string },
  eventId: string,
  tab: EventTabKey,
  extra?: Record<string, string>,
) {
  if (!surface.pathname.startsWith('/event/public/')) {
    const mode = new URLSearchParams(surface.search).get('modus')
    return eventHref(eventId, tab, { ...(mode ? { modus: mode } : {}), ...extra })
  }
  const params = new URLSearchParams(surface.search)
  for (const key of [EVENT_FORM_PARAM, EVENT_GROUPING_PARAM, 'section']) params.delete(key)
  params.set('t', EVENT_TAB_SLUGS[tab])
  for (const [key, value] of Object.entries(extra ?? {})) params.set(key, value)
  return `${surface.pathname}?${params}`
}

/**
 * The event's invitation link: the no-account entry (/event/public/<token>) that the organiser
 * shares, and where a guest answers or signs up for a date. One builder for every place that
 * shows or copies it (29.09.2026; OfferDetailPage and NewBroadcastTab each had their own).
 */
export function eventInvitationUrl(token: string, slug?: string | null, origin = window.location.origin): string {
  return `${origin}/event/public/${token}${slug ? `/${slug}` : ''}`
}

